import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as Tabs from '@radix-ui/react-tabs';
import { Plus, RefreshCw, Search, Store, UserRound, CalendarDays, Check, ArrowUpRight, ClipboardList, Clock3, MessageSquare, History, AlertCircle, Loader2, CheckCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { NativeSelect } from '../../components/ui/native-select';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import './tasks.css';
import { header } from '../../components/header.encry';
import { getAuthData } from '../../utils/authStorage';
import { STATUS_LABELS, PRIORITY_LABELS, idOf, personName, userStoreIds, canEditTask, filterTasks } from './taskPresentation';

const API = process.env.REACT_APP_PEGASUS_API_URL || 'https://api.pegasus.le-celadon.ma';
const EMPTY = { title: '', description: '', status: 'backlog', priority: 'medium', targetType: 'user', store_id: '', assignees: [], dueDate: '' };
const ACTIONS = { created: 'a créé la tâche', updated: 'a modifié la tâche', status_changed: 'a changé le statut',
  claimed: 'a pris en charge la tâche', commented: 'a ajouté un commentaire', archived: 'a archivé la tâche' };
const dateLabel = date => date ? new Date(date).toLocaleString('fr-FR') : '';
const plainText = html => {
  if (!html) return '';
  return new DOMParser().parseFromString(html, 'text/html').body.textContent || '';
};
const errorLabel = error => error.response?.data?.error || 'La demande a échoué. Veuillez réessayer.';
const StatusBadge = ({ status }) => <Badge tone={status === 'done' ? 'success' : status === 'inprogress' ? 'accent' : status === 'canceled' ? 'coral' : 'neutral'}>
  <span className="tasks-badge-dot" />{STATUS_LABELS[status] || status}
</Badge>;
const Notice = ({ children, error = false, action }) => <div className="tasks-notice" data-error={error} role={error ? 'alert' : 'status'}>
  <AlertCircle size={16} aria-hidden="true" /><div>{children}</div>{action}
</div>;

export default function ManagementTasks() {
  const [tasks, setTasks] = useState([]);
  const [options, setOptions] = useState({ users: [], stores: [], currentUserId: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [scope, setScope] = useState('all');
  const [store, setStore] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [form, setForm] = useState(null);
  const [formError, setFormError] = useState('');
  const [detailId, setDetailId] = useState('');
  const [comment, setComment] = useState('');
  const auth = getAuthData() || {};
  const roles = auth.roles || [];
  const userId = options.currentUserId;
  const detail = tasks.find(task => idOf(task) === detailId);
  const storeIds = options.stores.map(idOf);
  const editable = task => canEditTask(task, userId, roles, storeIds, options.managedUserIds || []);

  async function reload() {
    setError('');
    setLoading(true);
    try {
      const [taskResponse, optionResponse] = await Promise.all([
        axios.get(`${API}/tasks`, header()), axios.get(`${API}/tasks/options`, header())
      ]);
      if (!Array.isArray(taskResponse.data) || !Array.isArray(optionResponse.data?.stores) || !Array.isArray(optionResponse.data?.users)) {
        throw new Error('Invalid task response');
      }
      setTasks(taskResponse.data);
      setOptions(optionResponse.data);
    } catch (e) { setError(errorLabel(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => {
    let active = true;
    Promise.all([axios.get(`${API}/tasks`, header()), axios.get(`${API}/tasks/options`, header())])
      .then(([taskResponse, optionResponse]) => {
        if (!Array.isArray(taskResponse.data) || !Array.isArray(optionResponse.data?.stores) || !Array.isArray(optionResponse.data?.users)) throw new Error('Invalid task response');
        if (active) { setTasks(taskResponse.data); setOptions(optionResponse.data); }
      }).catch(e => { if (active) setError(errorLabel(e)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  async function mutate(method, path, body) {
    setBusy(true);
    setError('');
    try {
      const response = await axios({ method, url: `${API}${path}`, data: body, ...header() });
      const task = response.data;
      if (!task?._id) throw new Error('Invalid task response');
      setTasks(previous => task.archivedAt ? previous.filter(item => idOf(item) !== idOf(task))
        : previous.some(item => idOf(item) === idOf(task)) ? previous.map(item => idOf(item) === idOf(task) ? task : item) : [task, ...previous]);
      return true;
    } catch (e) {
      setError(errorLabel(e));
      if (form) setFormError(errorLabel(e));
      return false;
    } finally { setBusy(false); }
  }
  function openForm(task) {
    setDetailId('');
    setFormError('');
    setForm(task ? { ...EMPTY, ...task, description: plainText(task.description), store_id: idOf(task.store_id),
      assignees: (task.assignees || []).map(idOf), dueDate: task.dueDate ? new Date(task.dueDate).toISOString().slice(0, 10) : '' }
      : { ...EMPTY, store_id: store, assignees: userId ? [idOf(userId)] : [] });
  }
  async function save(event) {
    event.preventDefault();
    if (!form.title.trim() || (form.targetType === 'store' && !form.store_id) || (form.targetType === 'user' && !form.assignees.length)) {
      setFormError('Saisissez le titre et choisissez les destinataires ou le magasin.'); return;
    }
    const payload = Object.fromEntries(Object.keys(EMPTY).map(key => [key, form[key]]));
    payload.store_id = payload.store_id || null;
    payload.dueDate = payload.dueDate || null;
    if (payload.targetType === 'store') payload.assignees = [];
    if (await mutate(form._id ? 'put' : 'post', form._id ? `/tasks/${form._id}` : '/tasks', payload)) setForm(null);
  }
  const filtered = filterTasks(tasks, { scope, userId, store, status, search });
  const eligibleUsers = options.users.filter(user => form?.store_id ? userStoreIds(user).includes(form.store_id)
    : options.canManage || roles.includes('admin') || idOf(user) === idOf(userId));

  const counts = {
    backlog: tasks.filter(task => task.status === 'backlog').length,
    inprogress: tasks.filter(task => task.status === 'inprogress').length,
    done: tasks.filter(task => task.status === 'done').length,
  };
  return <section className="pegasus-ui tasks-page" aria-labelledby="tasks-title">
    <div className="tasks-heading">
      <div><div className="tasks-eyebrow">Management / Votre équipe</div>
        <h1 id="tasks-title">Les tâches, <span>en équipe.</span></h1>
        <p>Travail personnel et tâches partagées avec les équipes magasin.</p>
      </div>
      <div className="tasks-heading-actions">
        <Button variant="outline" disabled={loading || busy} onClick={reload}><RefreshCw size={15} className={loading ? 'tasks-spin' : ''} aria-hidden="true" />Actualiser</Button>
        <Button disabled={loading || busy || !userId} onClick={() => openForm()}><Plus size={16} aria-hidden="true" />Nouvelle tâche</Button>
      </div>
    </div>
    {error && <Notice error action={<Button variant="ghost" size="sm" onClick={reload} disabled={busy || loading}>Réessayer</Button>}>{error}</Notice>}
    <div className="tasks-summary" aria-label="Résumé des tâches">
      {[
        ['backlog', 'À faire', ClipboardList, 'Les prochaines étapes'],
        ['inprogress', 'En cours', Clock3, 'Le travail avance'],
        ['done', 'Terminées', CheckCheck, 'Un effort collectif'],
      ].map(([key, label, Icon, caption]) => <Card key={key} className="tasks-summary-card">
        <div className="tasks-summary-icon" data-status={key}><Icon size={19} aria-hidden="true" /></div>
        <div><span>{label}</span><strong>{loading ? '—' : counts[key]}</strong><small>{caption}</small></div>
      </Card>)}
    </div>
    <Tabs.Root value={scope} onValueChange={setScope}>
      <div className="tasks-toolbar">
        <Tabs.List className="tasks-tabs" aria-label="Vue des tâches">
          <Tabs.Trigger value="all">Toutes mes tâches accessibles</Tabs.Trigger>
          <Tabs.Trigger value="mine"><UserRound size={14} aria-hidden="true" />Mes tâches directes</Tabs.Trigger>
          <Tabs.Trigger value="store"><Store size={14} aria-hidden="true" />Tâches magasin</Tabs.Trigger>
        </Tabs.List>
        <div className="tasks-filters">
          <div className="tasks-search"><Search size={16} aria-hidden="true" /><Input aria-label="Rechercher une tâche" placeholder="Rechercher une tâche…" value={search} onChange={e => setSearch(e.target.value)} /></div>
          <div><label className="sc-sr-only" htmlFor="tasks-filter-store">Magasin</label><NativeSelect id="tasks-filter-store" value={store} onChange={e => setStore(e.target.value)}>
            <option value="">Tous mes magasins</option>{options.stores.map(item => <option key={idOf(item)} value={idOf(item)}>{item.name}</option>)}
          </NativeSelect></div>
          <div><label className="sc-sr-only" htmlFor="tasks-filter-status">Statut</label><NativeSelect id="tasks-filter-status" value={status} onChange={e => setStatus(e.target.value)}>
            <option value="">Tous les statuts</option>{Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </NativeSelect></div>
        </div>
      </div>
      <Tabs.Content value={scope} className="tasks-results">
        {loading ? <div className="tasks-empty" role="status"><Loader2 className="tasks-spin" size={25} /><h2>Chargement des tâches</h2><p>Votre espace de travail se prépare.</p></div> : <>
          <div className="tasks-result-heading"><span>{filtered.length} tâche(s)</span><span>Chaque contribution compte.</span></div>
          {!filtered.length && <Card className="tasks-empty"><ClipboardList size={30} aria-hidden="true" /><h2>Aucune tâche pour le moment</h2><p>Aucune tâche ne correspond à cette sélection.</p></Card>}
          <div className="tasks-grid">
            {filtered.map(task => <Card key={task._id} className="tasks-card">
              <CardContent className="tasks-card-content">
                <div className="tasks-card-badges"><StatusBadge status={task.status} /><Badge tone={['high', 'urgent'].includes(task.priority) ? 'coral' : 'outline'}>{PRIORITY_LABELS[task.priority] || task.priority}</Badge></div>
                <h2>{task.title}</h2>
                <div className="tasks-card-target">
                  {task.targetType === 'store' ? <Store size={15} aria-hidden="true" /> : <UserRound size={15} aria-hidden="true" />}
                  <span>{task.targetType === 'store' ? `Équipe : ${task.store_id?.name || 'Magasin'}` : `Pour : ${(task.assignees || []).map(personName).join(', ') || 'Non affectée'}`}</span>
                </div>
                {task.targetType !== 'store' && task.store_id?.name && <div className="tasks-card-meta">{task.store_id.name}</div>}
                {task.dueDate && <div className="tasks-card-meta"><CalendarDays size={14} aria-hidden="true" />Échéance : {new Date(task.dueDate).toLocaleDateString('fr-FR')}</div>}
                {task.claimedBy && <div className="tasks-card-meta"><UserRound size={14} aria-hidden="true" />Prise en charge : {personName(task.claimedBy)}</div>}
                {task.completedBy && <div className="tasks-card-completed"><CheckCheck size={15} aria-hidden="true" /><span>Terminée par {personName(task.completedBy)}</span></div>}
              </CardContent>
              <div className="tasks-card-footer">
                <Button variant="ghost" size="sm" onClick={() => { setDetailId(task._id); setComment(''); }}>Détails et historique<ArrowUpRight size={14} aria-hidden="true" /></Button>
                {!['done', 'canceled'].includes(task.status) && <Button size="sm" disabled={busy} onClick={() => mutate('put', `/tasks/${task._id}`, { status: 'done' })}><Check size={14} aria-hidden="true" />Terminer</Button>}
              </div>
            </Card>)}
          </div>
        </>}
      </Tabs.Content>
    </Tabs.Root>

    <Dialog open={Boolean(form)} onOpenChange={open => { if (!open && !busy) setForm(null); }}>
      <DialogContent closeDisabled={busy} aria-describedby="tasks-form-description">
        {form && <form className="pegasus-dialog-form" onSubmit={save}>
          <div className="pegasus-dialog-header"><DialogTitle>{form._id ? 'Modifier la tâche' : 'Nouvelle tâche'}</DialogTitle>
            <DialogDescription id="tasks-form-description">Un objectif clair, les bonnes personnes et un suivi partagé.</DialogDescription></div>
          <div className="pegasus-dialog-body">
            {formError && <Notice error>{formError}</Notice>}
            <fieldset className="tasks-form-fields" disabled={busy}>
              <div className="tasks-field"><label htmlFor="tasks-form-title">Titre <span>*</span></label><Input id="tasks-form-title" required maxLength={200} value={form.title} placeholder="Que faut-il accomplir ?" onChange={e => setForm({ ...form, title: e.target.value })} /></div>
              <div className="tasks-field"><label htmlFor="tasks-form-description-input">Description</label><Textarea id="tasks-form-description-input" rows={3} maxLength={20000} value={form.description} placeholder="Contexte, consignes et informations utiles…" onChange={e => setForm({ ...form, description: e.target.value })} /></div>
              <div className="tasks-form-grid">
                <div className="tasks-field"><label htmlFor="tasks-form-target">Affectation</label><NativeSelect id="tasks-form-target" value={form.targetType} onChange={e => setForm({ ...form, targetType: e.target.value, assignees: e.target.value === 'user' ? [idOf(userId)] : [] })}>
                  <option value="user">Directement à une personne</option><option value="store">À toute l'équipe du magasin</option>
                </NativeSelect></div>
                <div className="tasks-field"><label htmlFor="tasks-form-store">Magasin {form.targetType === 'store' && <span>*</span>}</label><NativeSelect id="tasks-form-store" required={form.targetType === 'store'} value={form.store_id}
                  onChange={e => setForm({ ...form, store_id: e.target.value, assignees: [] })}>
                  <option value="">Sans magasin (tâche personnelle)</option>{options.stores.map(item => <option key={idOf(item)} value={idOf(item)}>{item.name}</option>)}
                  {form.store_id && !options.stores.some(item => idOf(item) === form.store_id) && <option value={form.store_id}>
                    {tasks.find(item => idOf(item) === form._id)?.store_id?.name || 'Magasin actuel'} (affectation existante)
                  </option>}
                </NativeSelect></div>
              </div>
              {form.targetType === 'store' ? <Notice>Tous les membres de ce magasin pourront voir, prendre en charge et terminer cette tâche.</Notice>
                : <fieldset className="tasks-recipients"><legend>Destinataires <span>*</span></legend>
                  <div className="tasks-recipient-list">{eligibleUsers.map(user => <label key={idOf(user)}>
                    <input type="checkbox" checked={form.assignees.includes(idOf(user))} onChange={e => setForm({ ...form,
                      assignees: e.target.checked ? [...form.assignees, idOf(user)] : form.assignees.filter(id => id !== idOf(user)) })} />
                    <span className="tasks-recipient-avatar" aria-hidden="true">{personName(user).slice(0, 1)}</span><span>{personName(user)}</span>
                  </label>)}</div>
                  {!eligibleUsers.length && <p>Aucun destinataire disponible pour ce magasin.</p>}
                  <small>Sélectionnez une ou plusieurs personnes du magasin.</small>
                </fieldset>}
              <div className="tasks-form-grid">
                <div className="tasks-field"><label htmlFor="tasks-form-priority">Priorité</label><NativeSelect id="tasks-form-priority" value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })}>
                  {Object.entries(PRIORITY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </NativeSelect></div>
                <div className="tasks-field"><label htmlFor="tasks-form-date">Échéance</label><Input id="tasks-form-date" type="date" value={form.dueDate} onChange={e => setForm({ ...form, dueDate: e.target.value })} /></div>
              </div>
            </fieldset>
          </div>
          <div className="pegasus-dialog-footer"><Button variant="outline" disabled={busy} onClick={() => setForm(null)}>Annuler</Button><Button type="submit" disabled={busy}>{busy ? 'Enregistrement…' : 'Enregistrer'}</Button></div>
        </form>}
      </DialogContent>
    </Dialog>

    <Dialog open={Boolean(detail)} onOpenChange={open => { if (!open && !busy) setDetailId(''); }}>
      <DialogContent className="tasks-detail-dialog" closeDisabled={busy} aria-describedby="tasks-detail-description">
        {detail && <>
          <div className="pegasus-dialog-header"><DialogTitle>{detail.title}</DialogTitle>
            <DialogDescription id="tasks-detail-description">Créée par {personName(detail.createdBy)} le {dateLabel(detail.createdAt)}</DialogDescription></div>
          <div className="pegasus-dialog-body">
            {error && <Notice error>{error}</Notice>}
            <div className="tasks-detail-top">
              <div><StatusBadge status={detail.status} /><p className="tasks-description">{plainText(detail.description) || 'Aucune description.'}</p>
                <p className="tasks-detail-target">{detail.targetType === 'store' ? <Store size={15} aria-hidden="true" /> : <UserRound size={15} aria-hidden="true" />}
                  {detail.targetType === 'store' ? `Équipe : ${detail.store_id?.name || 'Magasin'}` : `Destinataires : ${(detail.assignees || []).map(personName).join(', ')}`}</p>
                {detail.claimedBy && <Badge tone="accent">Prise en charge : {personName(detail.claimedBy)}</Badge>}
              </div>
              <div className="tasks-field"><label htmlFor="tasks-detail-status">Statut</label><NativeSelect id="tasks-detail-status" disabled={busy} value={detail.status}
                onChange={e => mutate('put', `/tasks/${detail._id}`, { status: e.target.value })}>
                {Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </NativeSelect></div>
            </div>
            <div className="tasks-detail-actions">
              {!detail.claimedBy && !['done', 'canceled'].includes(detail.status) && <Button disabled={busy} variant="outline" onClick={() => mutate('post', `/tasks/${detail._id}/claim`, {})}><UserRound size={15} aria-hidden="true" />Prendre en charge</Button>}
              {editable(detail) && <Button variant="outline" disabled={busy} onClick={() => openForm(detail)}>Modifier les détails</Button>}
              {editable(detail) && <Button variant="ghost" className="tasks-archive-button" disabled={busy} onClick={async () => {
                if (window.confirm('Archiver cette tâche ? Son historique sera conservé.')) await mutate('delete', `/tasks/${detail._id}`);
              }}>Archiver</Button>}
            </div>
            <section className="tasks-history" aria-labelledby="tasks-history-title">
              <h3 id="tasks-history-title"><History size={17} aria-hidden="true" />Historique</h3>
              {!detail.activity?.length && <p className="tasks-history-empty">Tâche ancienne : aucun historique enregistré.</p>}
              <ol className="tasks-timeline">{[...(detail.activity || [])].reverse().map((entry, index) => <li key={entry._id || index}>
                <div className="tasks-timeline-marker" aria-hidden="true">{entry.action === 'commented' ? <MessageSquare size={13} /> : <Check size={13} />}</div>
                <div><p><strong>{entry.actorName || personName(entry.user)}</strong> {ACTIONS[entry.action] || entry.action}</p>
                  <time dateTime={entry.date}>{dateLabel(entry.date)}</time>
                  {entry.changes?.status?.to && <div className="tasks-timeline-change">{STATUS_LABELS[entry.changes.status.from] || entry.changes.status.from} → {STATUS_LABELS[entry.changes.status.to] || entry.changes.status.to}</div>}
                  {entry.action === 'commented' && <div className="tasks-comment-text">{entry.changes?.text}</div>}
                  {entry.action === 'updated' && <small>Champs modifiés : {Object.keys(entry.changes || {}).join(', ')}</small>}
                </div>
              </li>)}</ol>
            </section>
            <div className="tasks-comment-box"><label htmlFor="tasks-comment">Ajouter un commentaire</label>
              <Textarea id="tasks-comment" rows={2} maxLength={4000} value={comment} onChange={e => setComment(e.target.value)} placeholder="Une précision ou une avancée à partager…" />
              <Button disabled={busy || !comment.trim()} size="sm" onClick={async () => {
                if (await mutate('post', `/tasks/${detail._id}/comments`, { text: comment })) setComment('');
              }}><MessageSquare size={14} aria-hidden="true" />Envoyer le commentaire</Button>
            </div>
          </div>
          <div className="pegasus-dialog-footer"><Button variant="outline" disabled={busy} onClick={() => setDetailId('')}>Fermer</Button></div>
        </>}
      </DialogContent>
    </Dialog>
  </section>;
}
